import React from 'react';
import { bool, func, object, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { ensureOwnListing } from '../../util/data';
import { findOptionsForSelectFilter } from '../../util/search';
import { LISTING_STATE_DRAFT } from '../../util/types';
import { ListingLink } from '..';
import { EditLiveRoomDetailsForm } from '../../forms';
import config from '../../config';

import css from './EditLiveRoomDetailsPanel.module.css';

const DEFAULT_AVAILABILITY_SEATS = 100;

const defaultAvailabilityPlanEntry = {
  seats: DEFAULT_AVAILABILITY_SEATS,
  startTime: '00:00',
  endTime: '00:00',
};

const defaultAvailabilityPlan = {
  type: 'availability-plan/time',
  timezone: 'Europe/Belgrade',
  entries: [
    {
      dayOfWeek: 'mon',
      ...defaultAvailabilityPlanEntry,
    },
    {
      dayOfWeek: 'tue',
      ...defaultAvailabilityPlanEntry,
    },
    {
      dayOfWeek: 'wed',
      ...defaultAvailabilityPlanEntry,
    },
    {
      dayOfWeek: 'thu',
      ...defaultAvailabilityPlanEntry,
    },
    {
      dayOfWeek: 'fri',
      ...defaultAvailabilityPlanEntry,
    },
    {
      dayOfWeek: 'sat',
      ...defaultAvailabilityPlanEntry,
    },
    {
      dayOfWeek: 'sun',
      ...defaultAvailabilityPlanEntry,
    },
  ],
};

const EditLiveRoomDetailsPanel = props => {
  const {
    className,
    rootClassName,
    history,
    listing,
    disabled,
    ready,
    onSubmit,
    onChange,
    submitButtonText,
    panelUpdated,
    updateInProgress,
    errors,
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureOwnListing(listing);
  const { title, description, availabilityPlan, publicData } = currentListing.attributes;

  const isPublished = currentListing.id && currentListing.attributes.state !== LISTING_STATE_DRAFT;
  const panelTitle = isPublished ? (
    <FormattedMessage
      id="EditLiveRoomDetailsPanel.title"
      values={{
        listingTitle: (
          <ListingLink className={css.listingLink} listing={listing} isLiveRoom>
            {title}
          </ListingLink>
        ),
      }}
    />
  ) : (
    <FormattedMessage id="EditLiveRoomDetailsPanel.createListingTitle" />
  );

  const type = publicData?.type || config.listingTypes['live-room'];

  const categoryOptions = findOptionsForSelectFilter('category', config.custom.filters);
  const themeOptions = findOptionsForSelectFilter('theme', config.custom.filters);
  const ageOptions = findOptionsForSelectFilter('age', config.custom.filters);
  const languageOptions = findOptionsForSelectFilter('language', config.custom.filters);

  return (
    <div className={classes}>
      <EditLiveRoomDetailsForm
        className={css.form}
        initialValues={{
          type,
          title,
          description,
          category: publicData?.category,
          theme: publicData?.theme,
          age: publicData?.age,
          language: publicData?.language,
        }}
        saveActionMsg={submitButtonText}
        onSubmit={values => {
          const { type, title, description, category, theme, language, age } = values;

          const updateValues = {
            title: title.trim(),
            description,
            availabilityPlan: availabilityPlan
              ? {
                  ...availabilityPlan,
                  entries: availabilityPlan.entries
                    ? availabilityPlan.entries
                    : defaultAvailabilityPlan.entries,
                }
              : defaultAvailabilityPlan,
            publicData: {
              type,
              category,
              theme,
              language,
              age,
            },
          };

          onSubmit(updateValues);
        }}
        onChange={onChange}
        disabled={disabled}
        ready={ready}
        updated={panelUpdated}
        updateInProgress={updateInProgress}
        fetchErrors={errors}
        history={history}
        panelTitle={panelTitle}
        listing={currentListing}
        categoryOptions={categoryOptions}
        themeOptions={themeOptions}
        ageOptions={ageOptions}
        languageOptions={languageOptions}
      />
    </div>
  );
};

EditLiveRoomDetailsPanel.defaultProps = {
  className: null,
  rootClassName: null,
  errors: null,
  listing: null,
};

EditLiveRoomDetailsPanel.propTypes = {
  className: string,
  rootClassName: string,

  // We cannot use propTypes.listing since the listing might be a draft.
  listing: object,

  disabled: bool.isRequired,
  ready: bool.isRequired,
  onSubmit: func.isRequired,
  onChange: func.isRequired,
  submitButtonText: string.isRequired,
  panelUpdated: bool.isRequired,
  updateInProgress: bool.isRequired,
  errors: object.isRequired,
};

export default EditLiveRoomDetailsPanel;
